import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Claims } from 'src/app/_models/common/claims';
import { Router } from "@angular/router";
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ClaimsService } from 'src/app/_services/common/claims.service';
import { NotificationService } from 'src/app/_services/common/notification.service';
import { OrgBasicFunctionSubscriptionService } from 'src/app/_services/billing-pricing/Org-BasicFunction-Subscription.service';
import { AuthenticationService } from 'src/app/_services';
import { ICONS } from 'src/app/_helpers/icons.constants';
import { ModalComponent } from 'src/app/shared/design-system/modal/modal.component';
import { ModalConfig } from "src/app/shared/design-system/modal/modal.modal";
import { ButtonComponent } from 'src/app/shared/design-system/button/button.component';
import { ButtonConfig } from 'src/app/shared/design-system/button/button.modal';
import { PurchaseOrderPanelComponent } from 'src/app/purchase-order/purchase-order-panel/purchase-order-panel.component';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'trial-end-alert-panel',
  templateUrl: './trial-end-alert-panel.component.html',
  standalone: true,
  imports: [TranslateModule, ModalComponent, ButtonComponent, PurchaseOrderPanelComponent]
})
export class TrialEndAlertPanelComponent implements OnInit, OnDestroy {

  public readonly ICONS = ICONS;

  private notificationSubscription: Subscription;

  claims!: Claims;
  /** A purchase order has been sent to the org -> show the PO panel instead of the alert. */
  showPurchaseOrderPanel = false;

  /**
   * Hide the PO panel while on the activation route so that, after returning from Stripe,
   * only the activation panel is visible (mirrors the nav-menu guard).
   */
  get canShowPurchaseOrderPanel(): boolean {
    return this.showPurchaseOrderPanel && !this.router.url.includes('purchase-order-activation');
  }
  /** The org has already requested a demo -> show the "request sent" state with only a Log out. */
  isDemoBooked = false;
  isRequesting = false;
  basicFunctionRemainingTrialDays!: number;
  modalConfig!: ModalConfig;
  requestDemoButton!: ButtonConfig;
  logoutButton!: ButtonConfig;
  basicFunctionTrialDays

  readonly calendlyUrl: string = environment.calendlyUrl;
  safeCalendlyUrl!: SafeResourceUrl;

  calendlyModalConfig: ModalConfig = {
    id: 'trial-end-calendly-modal',
    visible: false,
    title: '',
    size: 'xl',
    variant: 'default',
    headerIconSrc: ICONS.CALENDAR,
    hiddenCloseIcon: true,
  };

  calendlyConfirmButtonConfig!: ButtonConfig;

  constructor(
    private claimsService: ClaimsService,
    private router: Router,
    private orgBasicFunctionSubscriptionService: OrgBasicFunctionSubscriptionService,
    private authenticationService: AuthenticationService,
    private ngxService: NgxUiLoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer
  ) {
    this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
    });
  }

  ngOnInit(): void {
    this.safeCalendlyUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.calendlyUrl);

    // Listen for the real-time "PO sent" signal so a logged-in prospect is shown the PO immediately.
    this.fnListenForPurchaseOrderSent();

    // Purchase order already sent -> show the purchase order panel (no alert modal).
    if (this.claims?.IsPurchaseOrderPending) {
      this.showPurchaseOrderPanel = true;
      return;
    }
    this.isDemoBooked = !!this.claims?.IsDemoBooked;
    this.fnBuildConfigs();
    this.fnGetOrgBasicFunctionSubscriptionTrialDetails();
  }

  /** Real-time: when sales sends the PO, switch straight to the purchase order panel. */
  private fnListenForPurchaseOrderSent(): void {
    this.notificationSubscription = this.notificationService
      .onNotificationRaised()
      .subscribe((notification: any) => {
        if (notification?.text?.key === 'po_sent') {
          if (this.claims) {
            this.claims.IsPurchaseOrderPending = true;
            this.claimsService.updateClaims(this.claims);
          }
          this.showPurchaseOrderPanel = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.notificationSubscription?.unsubscribe();
  }

  fnGetOrgBasicFunctionSubscriptionTrialDetails(): void {
    if (!this.claims?.OrgId) {
      return;
    }
    this.orgBasicFunctionSubscriptionService
      .GetOrgBasicFunctionSubscriptionTrialDetails(this.claims.OrgId)
      .subscribe((res: any) => {
        if (res?.responseInfo) {
          this.basicFunctionRemainingTrialDays = res.responseInfo.basicFunctionRemainingTrialDays;
          this.basicFunctionTrialDays = res.responseInfo.basicFunctionTrialDays;
        }
      });
  }

  private fnBuildConfigs(): void {
    this.modalConfig = {
      visible: true,
      title: this.translate.instant('new_onb_trial_ended_title'),
      //variant: 'error',
      headerIconSrc: ICONS.ALERT_CIRCLE,
      headerIconAltOverride: this.translate.instant('new_onb_trial_ended_alt'),
      hiddenCloseIcon: true
    };

    this.requestDemoButton = {
      id: 'btnRequestDemo',
      label: this.translate.instant('new_onb_request_a_demo'),
      color: 'primary',
      size: 'md'
    };

    this.logoutButton = {
      id: 'btnLogout',
      label: this.translate.instant('new_onb_log_out'),
      color: 'primary',
      size: 'md'
    };

    this.calendlyModalConfig = { ...this.calendlyModalConfig, title: this.translate.instant('pricing_label_booknow') };

    this.calendlyConfirmButtonConfig = {
      id: 'calendly-confirm-btn',
      label: this.translate.instant('book_demo_confirm'),
      color: 'primary',
      size: 'md',
      disabled: false,
      ariaLabel: this.translate.instant('book_demo_confirm')
    };
  }

  /** Open the Calendly booking popup from the Request a Demo button (hides the alert behind it). */
  fnOpenCalendlyModal(): void {
    this.calendlyModalConfig = { ...this.calendlyModalConfig, visible: true };
    this.modalConfig = { ...this.modalConfig, visible: false };
  }

  fnCloseCalendlyModal(): void {
    this.calendlyModalConfig = { ...this.calendlyModalConfig, visible: false };
    this.modalConfig = { ...this.modalConfig, visible: true };
  }

  /** Dismissing the Calendly popup without confirming returns to the trial-end alert. */
  onCalendlyModalVisibleChange(visible: boolean): void {
    if (visible) {
      this.fnOpenCalendlyModal();
    } else {
      this.fnCloseCalendlyModal();
    }
  }

  /** Confirm from the Calendly popup: save the demo request, then show the "request sent" state (log out only). */
  fnConfirmDemoRequest(): void {
    if (this.isRequesting) {
      return;
    }
    this.isRequesting = true;
    this.ngxService.start();
    this.orgBasicFunctionSubscriptionService.BookDemo().subscribe({
      next: () => {
        this.claims.IsDemoBooked = true;
        this.claimsService.updateClaims(this.claims);
        this.isDemoBooked = true;
        this.isRequesting = false;
        this.ngxService.stop();
        this.fnCloseCalendlyModal();
      },
      error: () => {
        this.isRequesting = false;
        this.ngxService.stop();
        this.toastr.error(this.translate.instant('failed'));
        this.fnCloseCalendlyModal();
      }
    });
  }

  fnLogout(): void {
    this.authenticationService.logout();
    this.router.navigate(['/login']);
  }

  fnClosePurchaseOrderPanel(): void {
    this.showPurchaseOrderPanel = false;
  }
}
